import type { FragmentType } from 'src/gql';
import type { Household } from 'src/types/household';
import { datadogRum } from '@datadog/browser-rum';
import { isNil } from 'es-toolkit';
import { SquareArrowOutUpRight } from 'lucide-react';
import { TrackedButton as TrackedButtonV2 } from 'src/components/tracked';
import { ORION_URL } from 'src/constants/logo-urls/financial-institution-logo';
import { graphql, unmaskFragment } from 'src/gql';
import { getOrionHouseholdLink, getSalesforceHouseholdLink } from 'src/utils/advisor-dash-nav/links';
import { isClient } from 'src/utils/advisor-dash/household';
import { providerImgSrc } from 'src/utils/image';
import { DatadogRumAction } from 'src/utils/logging';

import AddSecondaryPortfolioButton from '../../admin/nav/add-secondary-portfolio-button';
import ConnectClientToOrionButton from '../../admin/nav/connect-client-to-orion-button';
import OpenDatadogButton from '../../admin/nav/open-datadog-button';
import UnlinkFromOrionButton from '../../admin/nav/unlink-from-orion-button';

export const AdminActionButtonsFragment = graphql(/* GraphQL */ `
  fragment AdminActionButtons on Household {
    householdStage
    orionHouseholdId
  }
`);

type AdminActionButtonsProps = {
  readonly household: Household;
  readonly householdFrag: FragmentType<typeof AdminActionButtonsFragment> | undefined;
};

function AdminActionButtons(props: AdminActionButtonsProps) {
  const { household: householdData, householdFrag } = props;
  const salesforceAccountId = householdData.salesforceAccountId || 'unknown';

  const household = unmaskFragment(AdminActionButtonsFragment, householdFrag);
  const orionHouseholdId = household?.orionHouseholdId;
  const accountReadyForOrionConnection = isClient(household?.householdStage) && isNil(orionHouseholdId);

  const getClientNames = () => {
    if (!householdData.primaryClient && !householdData.coClient) {
      return { primaryClientName: undefined, coClientName: undefined };
    }

    return {
      primaryClientName: householdData.primaryClient
        ? `${householdData.primaryClient.user.firstName} ${householdData.primaryClient.user.lastName}`
        : undefined,
      coClientName: householdData.coClient
        ? `${householdData.coClient.user.firstName} ${householdData.coClient.user.lastName}`
        : undefined,
    };
  };

  return (
    <>
      <TrackedButtonV2
        variant="secondary"
        elementName="admin-action-buttons-open-salesforce-button"
        render={<a href={getSalesforceHouseholdLink(salesforceAccountId)} target="_blank" />}
        onClick={() => {
          datadogRum.addAction(DatadogRumAction.OpenedInSalesforce, {
            accountId: salesforceAccountId,
          });
        }}
      >
        Open in Salesforce
        <SquareArrowOutUpRight />
      </TrackedButtonV2>
      {orionHouseholdId && (
        <TrackedButtonV2
          elementName="admin-action-buttons-open-orion-button"
          variant="secondary"
          render={<a href={getOrionHouseholdLink(orionHouseholdId.toString() || '')} target="_blank" />}
          onClick={() => {
            datadogRum.addAction(DatadogRumAction.OpenedInOrion, {
              orionHouseholdId,
            });
          }}
        >
          Open in Orion
          {/* eslint-disable-next-line @next/next/no-img-element -- providerImgSrc returns a tiny provider icon URL for button chrome */}
          <img src={providerImgSrc(ORION_URL)} alt="Orion" className="size-4 bg-inherit" />
        </TrackedButtonV2>
      )}
      {householdData.primaryClient?.user.id && accountReadyForOrionConnection && (
        <ConnectClientToOrionButton household={householdData} />
      )}
      {orionHouseholdId && <UnlinkFromOrionButton household={householdData} orionHouseholdId={orionHouseholdId} />}
      {orionHouseholdId && <AddSecondaryPortfolioButton savvyUserId={householdData.primaryClient?.user.id || ''} />}
      <OpenDatadogButton {...getClientNames()} />
    </>
  );
}

export default AdminActionButtons;
